import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, signal } from '@angular/core';
import { httpResource } from '@angular/common/http';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, map, startWith } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { NbAvatar, NbBottomNav, NbBottomNavItem, NbIcon, NbSideBrand, NbSideFoot, NbSideNavItem, NbSideNavSection, NbSidebar, NbToastHost, initials } from '@nabotavle/ui/core';
import { AuthService, roleSatisfies } from '@nabotavle/auth';
import { LocaleService } from '@nabotavle/util';
import { styreappUrl } from './styreapp-url';
import type { OwnProfile, TenantConfigView } from '@nabotavle/data-access/core';
import { Bell, Building2, CalendarDays, ClipboardList, Handshake, House, Inbox, Leaf, LifeBuoy, Megaphone, Menu, MessageCircle, PartyPopper, Search, ShieldAlert, Tag, User, Users, UsersRound, type IconNode } from 'lucide';
import { EmergencyStore } from '../features/beredskap/emergency-store';
import { EmergencyTakeover } from '../features/beredskap/emergency-takeover';

/**
 * The resident app shell (2026-09 prototype, decision B2: five equal tabs, no FAB —
 * «Meld sak» lives on Hjem, on Mine saker and in Mer). Chrome is route-driven via `data.chrome`:
 *  - `tab`      → lag-mark app bar (search + bell + user avatar) + bottom nav (Hjem/Oppslag/Booking/Chat/Mer).
 *  - `sub`      → no app bar (the screen renders its own `nb-sub-bar`) + bottom nav.
 *  - `composer` → no app bar + no bottom nav (the screen owns a footer composer).
 * Tablet (B5, ≥ 1024 px): a permanent sidebar (Hovedmeny · Nabolaget · Min side · Mer · Styret)
 * replaces the tab bar; the content column widens. Detected with matchMedia so only one nav is
 * in the DOM at a time.
 */
@Component({
  selector: 'nb-resident-shell',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterOutlet, NbBottomNav, NbBottomNavItem, NbAvatar, NbIcon, NbToastHost, NbSidebar, NbSideBrand, NbSideNavSection, NbSideNavItem, NbSideFoot, EmergencyTakeover, TranslatePipe],
  template: `<div class="frame">
    @if (wide()) {
      <nb-sidebar [ariaLabel]="'nav.main' | translate" class="side">
        <nb-side-brand [name]="lagName()" sub="Nabotavle" [mark]="lagInitials()" />
        @for (g of sideGroups(); track g.title) {
          <nb-side-nav-section>{{ g.title | translate }}</nb-side-nav-section>
          @for (item of g.items; track item.route) {
            <nb-side-nav-item [active]="isActive(item.route)" (activated)="go(item.route)"><nb-icon [icon]="item.icon" /> {{ item.label | translate }}</nb-side-nav-item>
          }
        }
        @if (isStyre()) {
          <nb-side-nav-section>{{ 'mer.board' | translate }}</nb-side-nav-section>
          <nb-side-nav-item (activated)="openStyreapp()"><nb-icon [icon]="ClipboardList" /> {{ 'mer.boardConsole' | translate }}</nb-side-nav-item>
        }
        <nb-side-foot>
          <button class="foot-user" type="button" (click)="go('/profil')">
            <nb-avatar size="s" [color]="1" [initials]="userInitials()" />
            <span class="grow"><span class="foot-name">{{ userName() }}</span><span class="foot-role">{{ unitLabel() }}</span></span>
          </button>
        </nb-side-foot>
      </nb-sidebar>
    }
    <div class="column">
    @if (chrome() === 'tab') {
      <header class="appbar">
        <span class="lagmark">{{ lagInitials() }}</span>
        <!-- Route data carries i18n KEYS; no key → the lag name / brand. -->
        <div><h1 class="lagname">{{ appTitleKey() ? (appTitleKey()! | translate) : lagName() }}</h1><div class="lagsub">{{ appSubKey() ? (appSubKey()! | translate) : 'Nabotavle' }}</div></div>
        <span class="spacer"></span>
        <button class="iconbtn" type="button" [attr.aria-label]="'appbar.search' | translate" (click)="go('/sok')">
          <nb-icon [icon]="Search" />
        </button>
        <button class="iconbtn" type="button" [attr.aria-label]="bellLabel()" (click)="go('/varsler')">
          <nb-icon [icon]="Bell" />
          @if (unread() > 0) { <span class="dot">{{ unread() }}</span> }
        </button>
        <button class="iconbtn av-btn" type="button" [attr.aria-label]="'common.myProfile' | translate" (click)="go('/profil')">
          <nb-avatar size="m" [color]="1" [initials]="userInitials()" />
        </button>
      </header>
    }

    <main class="content" [class.flush]="chrome() === 'composer'"><router-outlet /></main>

    @if (chrome() !== 'composer' && !wide()) {
      <nb-bottom-nav>
        <nb-bottom-nav-item [label]="'nav.home' | translate" [active]="isActive('/hjem')" (activated)="go('/hjem')">
          <nb-icon [icon]="House" />
        </nb-bottom-nav-item>
        <nb-bottom-nav-item [label]="'nav.oppslag' | translate" [active]="isActive('/oppslag')" (activated)="go('/oppslag')">
          <nb-icon [icon]="Megaphone" />
        </nb-bottom-nav-item>
        <nb-bottom-nav-item [label]="'nav.booking' | translate" [active]="isActive('/booking')" (activated)="go('/booking')">
          <nb-icon [icon]="CalendarDays" />
        </nb-bottom-nav-item>
        <nb-bottom-nav-item [label]="'nav.chat' | translate" [active]="isActive('/chat')" (activated)="go('/chat')">
          <nb-icon [icon]="MessageCircle" />
        </nb-bottom-nav-item>
        <nb-bottom-nav-item [label]="'nav.more' | translate" [active]="isActive('/mer')" (activated)="go('/mer')">
          <nb-icon [icon]="Menu" />
        </nb-bottom-nav-item>
      </nb-bottom-nav>
    }

    </div>
    </div>
    <!-- Global toast (ToastService) — sits above the tab bar, below the nødvarsel takeover. -->
    <nb-toast-host />
    <!-- Global nødvarsel breakthrough — overlays the whole shell when an alert is active. Always last. -->
    <nb-emergency-takeover />`,
  styles: `
    :host { display: block; min-block-size: 100dvh; background: var(--bg); }
    .frame { display: flex; min-block-size: 100dvh; }
    .column { flex: 1; min-inline-size: 0; display: flex; flex-direction: column; min-block-size: 100dvh; }
    .side { position: sticky; top: 0; block-size: 100dvh; }
    .side nb-side-nav-item { --nb-icon-size: 18px; }
    .foot-user { display: flex; align-items: center; gap: 10px; inline-size: 100%; background: none; border: 0; padding: 0; cursor: pointer; text-align: start; font: inherit; color: inherit; border-radius: var(--r-md); }
    .foot-user:focus-visible { outline: none; box-shadow: var(--ring); }
    .foot-user .grow { flex: 1; min-inline-size: 0; display: flex; flex-direction: column; }
    .foot-name { font-weight: 700; font-size: 13px; }
    .foot-role { font-size: 12px; color: var(--faint); }
    @media (min-width: 1024px) {
      .content { max-inline-size: 760px; padding: 24px 28px; }
      .appbar { padding-inline: 28px; }
    }
    .appbar { display: flex; align-items: center; gap: 11px; padding: 8px 18px 14px; padding-top: max(20px, calc(8px + var(--safe-top))); position: sticky; top: 0; z-index: 5; background: linear-gradient(180deg, var(--bg) 74%, transparent); }
    .lagmark { inline-size: 40px; block-size: 40px; border-radius: 13px; background: var(--brand); color: var(--on-brand); display: grid; place-items: center; font-family: var(--display); font-weight: 800; font-size: 14px; letter-spacing: 0.02em; flex-shrink: 0; }
    .lagname { font-weight: 800; font-size: 16px; line-height: 1.1; letter-spacing: -0.02em; margin: 0; } /* h1 for the landmark; sized like the old div */
    .lagsub { font-size: 10.5px; font-weight: 800; letter-spacing: 0.14em; text-transform: uppercase; color: var(--muted); margin-top: 2px; }
    .spacer { flex: 1; }
    .iconbtn { position: relative; inline-size: 40px; block-size: 40px; border-radius: 13px; border: none; background: none; color: var(--text); display: grid; place-items: center; cursor: pointer; --nb-icon-size: 22px; }
    .iconbtn::before { content: ''; position: absolute; inset: -2px; } /* 44×44 hit area */
    .iconbtn:focus-visible { outline: none; box-shadow: var(--ring); }
    .iconbtn:hover { background: var(--sunken); }
    .av-btn { padding: 0; }
    /* Avataren skal speile lag-merket (40px): design-systemets m-størrelse (46px)
       er for stor for 40px-knappen og gjør høyresiden ujevn mot venstresiden. */
    .av-btn nb-avatar { inline-size: 40px; block-size: 40px; font-size: 14px; }
    .dot { position: absolute; top: 2px; right: 2px; min-inline-size: 17px; block-size: 17px; padding: 0 4px; border-radius: 999px; background: var(--danger); color: var(--on-danger); font-size: 10px; font-weight: 800; display: grid; place-items: center; border: 2px solid var(--bg); }
    .content { flex: 1; padding: 16px; padding-block-end: 24px; max-inline-size: 640px; inline-size: 100%; margin-inline: auto; }
    .content.flush { padding-block-end: 16px; }
    nb-bottom-nav { position: sticky; bottom: 0; z-index: 5; }
  `,
})
export class ResidentShell {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly emergency = inject(EmergencyStore);
  private readonly auth = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly t = inject(TranslateService);
  private readonly localeService = inject(LocaleService);
  protected readonly ClipboardList = ClipboardList;
  protected readonly House = House;
  protected readonly Megaphone = Megaphone;
  protected readonly CalendarDays = CalendarDays;
  protected readonly MessageCircle = MessageCircle;
  protected readonly Menu = Menu;
  protected readonly Bell = Bell;
  protected readonly Search = Search;

  private readonly config = httpResource<TenantConfigView>(() => '/api/min-konfigurasjon');
  private readonly profile = httpResource<OwnProfile>(() => '/api/profil/me');
  private readonly unreadRes = httpResource<{ count: number }>(() => '/api/varsler/uleste-antall');

  protected readonly lagName = computed(() => { this.t.currentLang(); return this.config.value()?.branding?.displayName || this.t.instant('shell.lagFallback'); });
  protected readonly unread = computed(() => this.unreadRes.value()?.count ?? 0);
  protected readonly lagInitials = computed(() => initials(this.lagName()));
  protected readonly userName = computed(() => { this.t.currentLang(); return this.profile.value()?.displayName || this.t.instant('shell.userFallback'); });
  protected readonly userInitials = computed(() => initials(this.userName()));
  /** Bell name carries the unread count so screen readers hear it without the visual badge. */
  protected readonly bellLabel = computed(() => {
    this.t.currentLang();
    const n = this.unread();
    return n > 0 ? this.t.instant('appbar.notificationsUnread', { count: n }) : this.t.instant('appbar.notifications');
  });
  protected readonly unitLabel = computed(() => this.profile.value()?.unitLabel || '');
  protected readonly isStyre = computed(() => roleSatisfies(this.auth.activeRole(), 'Styremedlem'));

  /** Tablet/desktop breakpoint (B5): a permanent sidebar replaces the tab bar at ≥ 1024 px. */
  protected readonly wide = signal(typeof window !== 'undefined' && window.matchMedia?.('(min-width: 1024px)').matches);

  /** Sidebar groups (prototype tablet layout). Labels are i18n keys. */
  protected readonly sideGroups = computed<{ title: string; items: { label: string; icon: IconNode; route: string }[] }[]>(() => [
    { title: 'nav.main', items: [
      { label: 'nav.home', icon: House, route: '/hjem' },
      { label: 'nav.oppslag', icon: Megaphone, route: '/oppslag' },
      { label: 'nav.booking', icon: CalendarDays, route: '/booking' },
      { label: 'nav.chat', icon: MessageCircle, route: '/chat' },
    ] },
    { title: 'mer.sections.nabolaget', items: [
      { label: 'mer.links.naboer', icon: Users, route: '/naboer' },
      { label: 'mer.links.grupper', icon: UsersRound, route: '/grupper' },
      { label: 'mer.links.aktiviteter', icon: PartyPopper, route: '/aktiviteter' },
      { label: 'mer.links.torget', icon: Tag, route: '/torget' },
      { label: 'mer.links.nabodeling', icon: Handshake, route: '/nabodeling' },
    ] },
    { title: 'mer.sections.minSide', items: [
      { label: 'mer.links.minBolig', icon: Building2, route: '/min-bolig' },
      { label: 'mer.links.saker', icon: Inbox, route: '/saker' },
      { label: 'common.myProfile', icon: User, route: '/profil' },
    ] },
    { title: 'nav.more', items: [
      { label: 'mer.links.beredskap', icon: ShieldAlert, route: '/beredskap' },
      { label: 'mer.links.baerekraft', icon: Leaf, route: '/baerekraft' },
      { label: 'mer.links.support', icon: LifeBuoy, route: '/support' },
      { label: 'nav.allFeatures', icon: Menu, route: '/mer' },
    ] },
  ]);

  private readonly nav = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );

  private readonly leaf = computed(() => {
    this.nav();
    let r = this.route;
    while (r.firstChild) r = r.firstChild;
    return r.snapshot.data;
  });

  protected readonly chrome = computed<'tab' | 'sub' | 'composer'>(() => this.leaf()['chrome'] ?? 'sub');
  /** Route `data.appTitle` / `data.appSub` are translation keys (e.g. `nav.booking`, `shell.sub.booking`); undefined → lag name / «Nabotavle». */
  protected readonly appTitleKey = computed<string | undefined>(() => this.leaf()['appTitle']);
  protected readonly appSubKey = computed<string | undefined>(() => this.leaf()['appSub']);

  constructor() {
    // Server-side locale precedence (profile > local choice > lag default): applied once both resources
    // have settled. Guarded on the value pair so it never re-fires on an unrelated re-render — and never
    // fights a switch the user just made in Min profil (that changes neither value).
    let appliedKey: string | undefined;
    effect(() => {
      const profileSettled = this.profile.hasValue() || !!this.profile.error();
      const configSettled = this.config.hasValue() || !!this.config.error();
      if (!profileSettled || !configSettled) return;
      const profileLocale = this.profile.hasValue() ? this.profile.value()?.locale : undefined;
      const tenantDefault = this.config.hasValue() ? this.config.value()?.defaultLocale : undefined;
      const key = `${profileLocale ?? ''}|${tenantDefault ?? ''}`;
      if (key === appliedKey) return;
      appliedKey = key;
      this.localeService.applyServerPreference(profileLocale, tenantDefault);
    });

    // Open the live nødvarsel channel for the whole authenticated session.
    this.emergency.ensureStarted();
    // Track the tablet breakpoint (rotation / window resize).
    const mq = typeof window !== 'undefined' ? window.matchMedia?.('(min-width: 1024px)') : undefined;
    if (mq) {
      const onChange = (e: MediaQueryListEvent) => this.wide.set(e.matches);
      mq.addEventListener('change', onChange);
      this.destroyRef.onDestroy(() => mq.removeEventListener('change', onChange));
    }
  }

  /** The styre console is a separate app — deep-link across origins (B3: entry only). */
  protected openStyreapp(): void {
    location.assign(styreappUrl());
  }

  protected isActive(path: string): boolean {
    return this.nav().startsWith(path);
  }

  protected go(path: string): void {
    void this.router.navigateByUrl(path);
  }
}

